import {CategoryMenu, AnalyticsData, SubCategoryMenu, SubCategoryMenuBis, ProductType, ProductImpression} from '_types'

const getAnalyticsFirstLevel = (category: CategoryMenu, index: number) => {
    const analyticsObject: AnalyticsData = {} as AnalyticsData

    analyticsObject.mainMenu = category.analytics // solo categorie primo livello

    analyticsObject.firstLevelMenu = category.analytics // solo categorie primo livello

    analyticsObject.position = (index + 1).toString() // posizione della categoria nel menu solo per il primo livello

    // analyticsObject.analyticsTemplate = category?.analyticsTemplate || ''
    analyticsObject.analyticsTemplate = '' // rimuovere l'analytics template per il primo livello

    return analyticsObject
}

interface AnalyticsSecondLevelParams {
    category: SubCategoryMenu | CategoryMenu
    subcategory: SubCategoryMenuBis | null
    mainMenu: string
    analyticsTemplate: string
    position?: string
}

const getAnalyticsSecondLevel = ({category, subcategory, mainMenu, analyticsTemplate, position}: AnalyticsSecondLevelParams) => {
    const analyticsObject: AnalyticsData = {} as AnalyticsData
    analyticsObject.mainMenu = mainMenu
    analyticsObject.position = position
    analyticsObject.analyticsTemplate = analyticsTemplate
    analyticsObject.secondLevelMenu = category.analytics && position !== 'footer' ? category.analytics : 'cgid' in category ? category.cgid : undefined
    analyticsObject.finalLevelMenu = subcategory && subcategory.analytics ? subcategory.analytics : subcategory?.cgid
    return analyticsObject
}

const getLabel = (analyticsEventData: AnalyticsData) => {
    let label = analyticsEventData?.position || ''
    if (analyticsEventData?.secondLevelMenu) {
        label += `|${analyticsEventData.secondLevelMenu}`
    }
    if (analyticsEventData?.finalLevelMenu) {
        label += `|${analyticsEventData.finalLevelMenu}`
    }
    return label
}

const getHierarchy = (analyticsEventData: AnalyticsData) => {
    let hierarchy = analyticsEventData?.mainMenu || ''
    if (analyticsEventData?.position) {
        hierarchy += `|${analyticsEventData.position}`
    }
    if (analyticsEventData?.secondLevelMenu) {
        hierarchy += `|${analyticsEventData.secondLevelMenu}`
    }
    if (analyticsEventData?.finalLevelMenu) {
        hierarchy += `|${analyticsEventData.finalLevelMenu}`
    }
    return hierarchy
}

const getProductAnalyticsInfo = (product: ProductType) => {
    const productVariations = product?.c_variationGroupsData || product?.variationGroups
    const productVariationsArray = productVariations ? (Array.isArray(productVariations) ? productVariations : [productVariations]) : []
    const activeVariation = product?.c_variantData || productVariationsArray?.find((variation) => variation.default) || product?.c_setData
    const productPromotion = product?.productPromotions?.find((p) => p.promotionId.indexOf('sale') === 0)
    const productAnalyticsName = product?.c_analytics?.shortDescription || activeVariation?.analytics?.shortDescription
    const productAnalyticsPath = product?.c_analytics?.categoryPath || activeVariation?.analytics?.categoryPath
    const productAnalyticsProductId = product?.c_analytics?.productId || activeVariation?.analytics?.productId
    const productAnalyticsGender = product?.c_analytics?.gender || activeVariation?.analytics?.gender
    const productAnalyticsColorCode = product?.c_analytics?.color?.code || activeVariation?.analytics?.color?.code
    const productAnalyticsColorLabel = product?.c_analytics?.color?.label || activeVariation?.analytics?.color?.label
    const productCurrency = product?.c_price?.currency || product?.currency || product?.c_price?.currency || activeVariation?.pricing?.currency
    const isProductInStock = product?.c_analytics?.inStock ?? activeVariation?.analytics?.inStock ?? product?.analytics?.inStock
    const productLink = activeVariation?.pdpUrl || product?.c_productUrl
    const productTextTag = activeVariation?.textTag || product?.c_textTag
    const isProductSet = product?.hitType === 'set' || product?.type?.set
    const hasDiscount = product?.c_price?.hasDiscount
    const productPrice = activeVariation?.pricing?.price?.formattedValue || product?.c_price?.price?.formattedValue
    const discountedPrice = activeVariation?.pricing?.discount?.price?.formattedValue || product?.c_price?.discount?.price?.formattedValue
    const priceMinRange = activeVariation?.pricing?.priceRange?.minPrice?.formattedValue || product?.c_price?.priceRange?.minPrice?.formattedValue
    const discountedPriceMinRange =
        activeVariation?.pricing?.discount?.priceRange?.minPrice?.formattedValue || product?.c_price?.discount?.priceRange?.minPrice?.formattedValue

    const originalPriceNumber = activeVariation?.pricing?.price?.value || product?.c_price?.price?.value
    const discountedPriceNumber = activeVariation?.pricing?.discount?.price?.value || product?.c_price?.discount?.price?.value
    const productPriceNumber = discountedPriceNumber ?? originalPriceNumber
    let productAnalyticsPrice = hasDiscount ? discountedPriceNumber : originalPriceNumber
    productAnalyticsPrice = productAnalyticsPrice?.toFixed(2) ?? ''

    return {
        activeVariation,
        productPromotion,
        productAnalyticsName,
        productAnalyticsPath,
        productAnalyticsProductId,
        productAnalyticsGender,
        productAnalyticsColorCode,
        productAnalyticsColorLabel,
        productCurrency,
        productLink,
        productTextTag,
        productPrice,
        discountedPrice,
        priceMinRange,
        discountedPriceMinRange,
        productAnalyticsPrice,
        isProductInStock,
        isProductSet,
        hasDiscount,
        productPriceNumber,
    }
}

const getProductImpressionAnalytics = ({
    product,
    idx,
    tileAssetSize,
    isFullWidth = false,
    selectedSize = null,
    analyticsList,
    flow,
    page,
}: {
    product: ProductType
    idx?: number
    tileAssetSize?: number
    isFullWidth?: boolean
    selectedSize?: string | null
    analyticsList: string | undefined
    flow?: string
    page?: 'plp' | 'wishlist' | 'shoptheimage' | 'editorial-shoptheimage'
}): ProductImpression => {
    const {
        activeVariation,
        productAnalyticsName,
        productAnalyticsPath,
        productAnalyticsProductId,
        productAnalyticsGender,
        productAnalyticsColorCode,
        productAnalyticsColorLabel,
        productAnalyticsPrice,
        productTextTag,
        isProductInStock,
        isProductSet,
        hasDiscount,
    } = getProductAnalyticsInfo(product)

    const productVerticalVideoSrc = product?.c_videos?.vertical
    const hoverImageSrc = !productVerticalVideoSrc ? product?.c_images?.large?.[1]?.url : undefined
    const hasImageCarousel = product?.c_images?.showCarousel

    const analyticsProduct: ProductImpression = {
        name: !isProductSet ? productAnalyticsName : productAnalyticsProductId,
        price: !isProductSet ? productAnalyticsPrice : '',
        variant: !isProductSet ? productAnalyticsColorCode : '',
        color: !isProductSet ? productAnalyticsColorLabel : undefined,
        id: productAnalyticsProductId,
        category: productAnalyticsPath,
        callout: productTextTag,
        tileType: `${productVerticalVideoSrc ? 'video' : 'img'}_${isFullWidth ? 'double' : 'standard'}${hasImageCarousel ? '-imgswitch' : hoverImageSrc ? '-hover' : ''}`,
        gender: productAnalyticsGender,
        list: analyticsList || '',
        dimension26: String(!!hasDiscount),
    }

    if (flow === 'addToCart') {
        const selectedSizeObj = activeVariation?.variants?.find((variant) => variant.ID === selectedSize) || activeVariation
        analyticsProduct.dimension21 = selectedSizeObj?.analytics?.size?.label || ''
        analyticsProduct.quantity = 1
        analyticsProduct.dimension22 = page === 'shoptheimage' ? 'editorial-shoptheimage' : page
        analyticsProduct.dimension27 = Boolean(productVerticalVideoSrc).toString()
    } else {
        analyticsProduct.dimension14 = String(!!isProductInStock)

        if (idx !== undefined) {
            analyticsProduct.position = String(idx + 1)
        }
    }

    return analyticsProduct
}

/**
 * Normalizes PLP list names for editorial placements.
 * Expected input starts with "category-" and is converted to "editorial-".
 * @param {string | undefined} analyticsList - Raw PLP analytics list.
 * @returns {string} Normalized list for editorial placements.
 */
const toEditorialAnalyticsList = (analyticsList?: string) => {
    if (!analyticsList) return ''
    return analyticsList.startsWith('category-') ? `editorial-${analyticsList.slice('category-'.length)}` : analyticsList
}

export {
    getAnalyticsFirstLevel,
    getAnalyticsSecondLevel,
    getLabel,
    getHierarchy,
    getProductAnalyticsInfo,
    getProductImpressionAnalytics,
    toEditorialAnalyticsList,
}
